
import React, { useEffect } from "react";
import AppSidebar from "./AppSidebar";
import AppHeader from "./AppHeader";
import { SidebarProvider, useSidebar } from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { HomeScreenPrompt } from "@/components/ui/home-screen-prompt";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { PrivacyPolicyDialog } from "@/components/legal/PrivacyPolicyDialog";

interface AppLayoutProps {
  children: React.ReactNode;
}

const MainContent = ({ children }: { children: React.ReactNode }) => {
  const { state } = useSidebar();
  const isMobile = useIsMobile();
  
  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      if (event.message && /ResizeObserver loop/i.test(event.message)) {
        event.preventDefault();
        return true;
      }
      return false;
    };
    window.addEventListener('error', onError as EventListener);
    return () => window.removeEventListener('error', onError as EventListener);
  }, []);
  
  return (
    <div className="flex-1 flex flex-col min-h-screen overflow-x-hidden bg-gradient-to-br from-background via-background to-muted/30">
      {/* Header - show on both mobile and desktop now */}
      <AppHeader />
      
      <main className={`flex-1 w-full overflow-hidden flex flex-col ${isMobile ? 'pt-14' : 'pt-16'}`}>
        <div className={`${isMobile ? "p-4" : ""} animate-fadeIn`}>
          <ErrorBoundary>{children}</ErrorBoundary>
        </div>
      </main>
      
      {/* Desktop footer */}
      {!isMobile && (
        <footer className={`py-3 px-4 md:py-4 md:px-6 lg:px-8 xl:px-10 text-sm text-muted-foreground border-t bg-card/50 backdrop-blur-sm transition-all duration-300 ${
          state === "expanded" && !isMobile ? "opacity-0" : "opacity-100"
        }`}>
          <div className="w-full flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/lovable-uploads/a28d2ad7-ee8f-4f8e-a49c-83b00763d61c.png" 
                alt="OVIT Logo" 
                className="h-5 md:h-6 w-auto"
              />
              <span className="text-sm md:text-base font-medium text-crisp">Shiftask Security Management System By Ovit</span>
            </div>
            <PrivacyPolicyDialog />
          </div>
        </footer>
      )}
    </div>
  );
};

const AppLayout = ({ children }: AppLayoutProps) => {
  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-gradient-to-br from-background via-background to-muted/20 overflow-hidden">
        <AppSidebar />
        <MainContent>{children}</MainContent>
        <HomeScreenPrompt />
      </div>
    </SidebarProvider>
  );
};

export default AppLayout;
